type ProductPrivateInput = Pick<gen.ProductListingBoxFragment, 'id' | 'sellerId' | 'privateVisibility'> | Pick<gen.ProductListingFragment, 'id' | 'sellerId' | 'privateVisibility'>
export type ProductListingBoxFragmentFull = gen.ProductListingBoxFragment & { __private__: never }
export type ProductListingFragmentFull = gen.ProductListingFragment & { __private__: never }
type PricingMap = Record<string, gen.ProductListingPrivateFragment['values'] | null>
export type ProductPrivateFetchPolicy = 'cache' | 'cache-and-network'
export type ProductPricingType = 'private' | 'skip'
import { WatchQueryFetchPolicy } from '@apollo/client'

type PricingQueueItem = { id: string; fetchPolicy: ProductPrivateFetchPolicy }

// oxlint-disable-next-line typescript/explicit-module-boundary-types
export const usePrivatePricingState = () => {
  return useState('useCatalogPrivate', () => ({
    pricingMap: {} as PricingMap,
    // The queue of IDs waiting to be fetched
    queue: [] as PricingQueueItem[],
    isEnabled: false,
  }))
}

export function useCatalogPrivate(
  cb: () => None | ProductPrivateInput | ProductPrivateInput[],
  pricingType: ProductPricingType,
  _fetchPolicy?: WatchQueryFetchPolicy | None
): {
  toBoxes: (i: gen.ProductListingBoxFragment[] | None) => ProductListingBoxFragmentFull[]
  toBox: (i: gen.ProductListingBoxFragment | None) => ProductListingBoxFragmentFull | None
  toDoc: (i: gen.ProductListingFragment | None) => ProductListingFragmentFull | None
} {
  const fetchPolicy: ProductPrivateFetchPolicy = helpers.isIn(_fetchPolicy, 'cache-and-network', 'no-cache', 'network-only') ? 'cache-and-network' : 'cache'
  const auth = useAuth()

  const data = usePrivatePricingState()
  const cacheAndNetworkIds = new Set<string>()
  useReset(() => cacheAndNetworkIds.clear())
  const pricing = computed(() => {
    if (!auth.value.isAuth) return []

    return helpers.arr(auth.value.privatePricings, (i) => (i.hasPricing ? i.sellerId : undefined))
  })

  const idsToFetch = computed(() => {
    if (pricingType === 'skip') return
    if (!pricing.value.length) return

    return cb()
  })

  // 2. Watch for needed IDs and push to the Bridge
  watch(
    () => [idsToFetch.value, pricing.value] as const,
    ([_ids]) => {
      const ids = helpers.arrify(_ids)
      const b2b = pricing.value
      const activeIds = new Set<string>()

      for (const i of ids || []) {
        if (i && (i.privateVisibility || (i.sellerId && b2b.includes(i.sellerId)))) activeIds.add(i.id)
      }

      for (const id of cacheAndNetworkIds) {
        if (!activeIds.has(id)) cacheAndNetworkIds.delete(id)
      }

      if (!ids?.length) return

      const map = data.value.pricingMap
      const queue = [...data.value.queue]

      let process = false

      for (const i of ids) {
        // Logic: Is it private? Do we have access? Does its fetch policy require fetching?
        if (!i || !(i.privateVisibility || (i.sellerId && b2b.includes(i.sellerId)))) continue

        if (fetchPolicy === 'cache-and-network') {
          if (cacheAndNetworkIds.has(i.id)) continue
          cacheAndNetworkIds.add(i.id)
        } else if (i.id in map) {
          continue
        }

        const queueIndex = queue.findIndex((item) => item.id === i.id)

        if (queueIndex === -1) {
          queue.push({ id: i.id, fetchPolicy })
          process = true
        } else if (fetchPolicy === 'cache-and-network' && queue[queueIndex]!.fetchPolicy === 'cache') {
          queue[queueIndex] = { id: i.id, fetchPolicy }
          process = true
        }
      }

      if (process) {
        data.value.queue = queue
        data.value.isEnabled = true
      }
    },
    { immediate: true }
  )

  const hydrate = <T extends gen.ProductListingBoxFragment | gen.ProductListingFragment>(i: T | None, map: PricingMap, pricing: string[]): T | undefined => {
    const _values = !!auth.value.isAuth && !!i?.id && !!(i.privateVisibility || (i.sellerId && pricing.includes(i.sellerId))) ? map[i.id] : undefined
    return i && _values ? { ...i, _values } : i || undefined
  }

  return {
    toBoxes: (arr) => {
      const map = data.value.pricingMap
      const sellerIds = pricing.value
      return helpers.arr(arr, (i) => hydrate(i, map, sellerIds) as ProductListingBoxFragmentFull | None)
    },
    toBox: (i) => {
      return hydrate(i, data.value.pricingMap, pricing.value) as ProductListingBoxFragmentFull | None
    },
    toDoc: (i) => {
      return hydrate(i, data.value.pricingMap, pricing.value) as ProductListingFragmentFull | None
    },
  }
}
